import { useState, useEffect, useMemo, useCallback } from "react";
import constate from "constate";

import {
  getWorkOrderFields,
  getProductFields,
  getWorkOrderOpHistoryFields,
  getRouteFields,
  getOperationFields,
  getWorkerFields,
} from "../apis/workOrder";
import { allFields } from "../utils/blueprint";

const useMappingFields = () => {
  const [workOrderRequiredFields, setWorkOrderRequiredFields] = useState();
  const [workOrderOptionalFields, setWorkOrderOptionalFields] = useState();
  const [productRequiredFields, setProductRequiredFields] = useState();
  const [productOptionalFields, setProductOptionalFields] = useState();
  const [
    workOrderOpHistoryRequiredFields,
    setWorkOrderOpHistoryRequiredFields,
  ] = useState();
  const [
    workOrderOpHistoryOptionalFields,
    setWorkOrderOpHistoryOptionalFields,
  ] = useState();
  const [routeRequiredFields, setRouteRequiredFields] = useState();
  const [routeOptionalFields, setRouteOptionalFields] = useState();
  const [opRequiredFields, setOpRequiredFields] = useState();
  const [opOptionalFields, setOpOptionalFields] = useState();
  const [workerRequiredFields, setWorkerRequiredFields] = useState();
  const [workerOptionalFields, setWorkerOptionalFields] = useState();

  const equipmentFields = useMemo(() => ["device_name", "station_name"], []);

  function filterCols(sourceArr, excludedArr, otherExcluded) {
    return sourceArr?.filter(
      (item) =>
        !item.includes("uuid") &&
        item !== "tenant_id" &&
        item !== "progress" &&
        !excludedArr?.includes(item) &&
        (otherExcluded ? !item.includes(otherExcluded) : true)
    );
  }

  function keysToObjectArr(keys) {
    return keys.map(
      (fieldKey) =>
        allFields[fieldKey] || {
          key: fieldKey,
          label: fieldKey,
          description: "",
        }
    );
  }

  const getAllFields = useCallback(async () => {
    let rProductFields = [];
    let rWoFields = [];
    let rRouteFields = [];
    let rOpFields = [];
    let rWorkerFields = [];
    let rWoohFields = [];

    /* --- product --- */
    const productData = await getProductFields();
    rProductFields = filterCols(productData["data"]["required"]);
    rProductFields = rProductFields.map((field) => {
      if (!field.includes("product")) {
        return "product_" + field;
      }
      return field;
    });
    const oProductFields = filterCols(
      Object.keys(productData["data"]["properties"]),
      productData["data"]["required"],
      "time"
    );
    setProductRequiredFields(keysToObjectArr(rProductFields));
    setProductOptionalFields(keysToObjectArr(oProductFields));

    /* --- wo --- */
    const woData = await getWorkOrderFields();
    rWoFields = filterCols(woData["data"]["required"]);
    const oWoFields = filterCols(
      Object.keys(woData["data"]["properties"]),
      woData["data"]["required"]
    );
    setWorkOrderRequiredFields(keysToObjectArr(rWoFields));
    setWorkOrderOptionalFields(keysToObjectArr(oWoFields));

    /* --- route --- */
    const routeData = await getRouteFields();
    rRouteFields = filterCols(routeData["data"]["required"]);
    rRouteFields = rRouteFields.map((field) => {
      if (!field.includes("route")) {
        return "route_" + field;
      }
      return field;
    });
    const oRouteFields = filterCols(
      Object.keys(routeData["data"]["properties"]),
      routeData["data"]["required"],
      "time"
    );
    setRouteRequiredFields(keysToObjectArr(rRouteFields));
    setRouteOptionalFields(keysToObjectArr(oRouteFields));

    /* --- op --- */
    const opData = await getOperationFields();
    rOpFields = filterCols([...opData["data"]["required"], "op_order"]);
    rOpFields = rOpFields.map((field) => {
      if (!field.includes("op")) {
        return "op_" + field;
      }
      return field;
    });
    const oOpFields = filterCols(
      Object.keys(opData["data"]["properties"]),
      opData["data"]["required"],
      "time"
    );
    setOpRequiredFields(keysToObjectArr(rOpFields));
    setOpOptionalFields(keysToObjectArr(oOpFields));

    /* --- worker --- */
    const workerData = await getWorkerFields();
    rWorkerFields = filterCols(workerData["data"]["required"]);
    rWorkerFields = rWorkerFields.map((field) => {
      if (!field.includes("worker")) {
        return "worker_" + field;
      }
      return field;
    });
    const oWorkerFields = filterCols(
      Object.keys(workerData["data"]["properties"]),
      workerData["data"]["required"],
      "time"
    );
    setWorkerRequiredFields(keysToObjectArr(rWorkerFields));
    setWorkerOptionalFields(keysToObjectArr(oWorkerFields));

    /* --- wooh --- */
    const woohData = await getWorkOrderOpHistoryFields();
    rWoohFields = filterCols(woohData["data"]["required"], [
      ...equipmentFields,
      ...rProductFields,
      ...rRouteFields,
      ...rOpFields,
      ...rWorkerFields,
      "work_order_id",
    ]);
    const oWoohFields = filterCols(
      Object.keys(woohData["data"]["properties"]),
      [
        ...woohData["data"]["required"],
        ...equipmentFields,
        ...rProductFields,
        ...rRouteFields,
        ...rOpFields,
        ...rWorkerFields,
      ],
      "work_order_id"
    );
    setWorkOrderOpHistoryRequiredFields(keysToObjectArr(rWoohFields));
    setWorkOrderOpHistoryOptionalFields(keysToObjectArr(oWoohFields));
  }, [equipmentFields]);

  useEffect(() => {
    getAllFields();
  }, [getAllFields]);

  const equipmentRequiredFields = keysToObjectArr(equipmentFields);

  return {
    workOrderRequiredFields,
    workOrderOptionalFields,
    productRequiredFields,
    productOptionalFields,
    workOrderOpHistoryRequiredFields,
    workOrderOpHistoryOptionalFields,
    routeRequiredFields,
    routeOptionalFields,
    opRequiredFields,
    opOptionalFields,
    equipmentRequiredFields,
    workerRequiredFields,
    workerOptionalFields,
  };
};

export const [MappingFieldsProvider, useMappingFieldsContext] =
  constate(useMappingFields);
